import React, { useCallback, useMemo, useRef, useState } from "react";
import { useModal } from "react-modal-hook";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  GetWorkflowQuery,
  useUpdateWorkflowStepCollaboratorsMutation,
  useWorkflowStepCollaboratorsQuery,
  WorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { spacing } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { CommonModal } from "../Common/CommonModal";
import { Loading } from "../Common/Icons/Loading";
import Modal from "../Common/Modal";
import {
  Collaborator,
  InheritedPermissionGroup,
  PermissionOption,
  ReadOnlyCollaboratorRow,
  ShareForm,
} from "../Common/ShareForm";
import { CollaboratorListContainer } from "../Common/ShareForm/__styles__/CollaboratorList";
import { Body } from "../Common/Typography";

type ModalView = "main" | "inheritedPermissions";

type StepCollaborator = NonNullable<
  WorkflowStepCollaboratorsQuery["workflowStep"]
>["collaborators"][number];

const WORKFLOW_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "createResource", label: "Can create" },
  { value: "updateResource", label: "Can update" },
  { value: "viewResource", label: "Can view" },
];

const mapIndividualCollaboratorsFromStep = (
  collaborators: StepCollaborator[],
): Collaborator[] => {
  if (!collaborators) return [];

  const individualCollaborators = collaborators.filter(
    (
      collab,
    ): collab is Extract<
      StepCollaborator,
      { __typename?: "WorkflowCollaborator" }
    > => collab.__typename === "WorkflowCollaborator",
  );

  const permissionRank: Record<string, number> = {
    createResource: 3,
    updateResource: 2,
    viewResource: 1,
  };

  const collaboratorMap = new Map<string, Collaborator>();

  individualCollaborators.forEach(collab => {
    const user = collab.publicUser;
    const name = user ? `${user.firstName} ${user.lastName}`.trim() : "Unknown";
    const description = user?.email;

    const existing = collaboratorMap.get(collab.entityId);
    const currentRank = permissionRank[collab.policy] || 0;
    const existingRank =
      existing ? permissionRank[existing.permission] || 0 : -1;

    if (currentRank > existingRank) {
      collaboratorMap.set(collab.entityId, {
        id: collab.entityId,
        name: name || description || "Unknown",
        description,
        permission: collab.policy,
      });
    }
  });

  return Array.from(collaboratorMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

const mapGroupPermissionsFromStep = (
  collaborators: StepCollaborator[],
): InheritedPermissionGroup[] => {
  if (!collaborators) return [];

  const groups: Array<
    Extract<StepCollaborator, { __typename?: "WorkflowStepCollaboratorGroup" }>
  > = [];

  for (const collab of collaborators) {
    if (collab.__typename === "WorkflowStepCollaboratorGroup") {
      groups.push(collab);
    }
  }

  return groups.map(group => ({
    id: group.id,
    label: `${group.name}s in the workflow`,
    collaborators: group.members.map(member => ({
      id: member.id,
      name: `${member.firstName} ${member.lastName}`.trim(),
      description: member.email,
      permission: group.policy,
    })),
  }));
};

interface InheritedPermissionsViewProps {
  entityName: string;
  group: InheritedPermissionGroup;
  onBack: () => void;
  onClose: () => void;
}

const InheritedPermissionsView = ({
  entityName,
  group,
  onBack,
  onClose,
}: InheritedPermissionsViewProps) => {
  return (
    <CommonModal
      title="Share workflow step"
      subtitle={entityName}
      size="large"
      onCancel={onClose}
      handlePrevious={onBack}
      content={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: spacing.l.value,
          }}
        >
          <Body
            type="regular"
            size="default"
            color="contentSecondary"
            style={{ paddingLeft: spacing.m.value }}
          >
            The following people have access to this step because they are
            members of the workflow.
          </Body>
          <CollaboratorListContainer>
            {group.collaborators.map(collaborator => (
              <ReadOnlyCollaboratorRow
                key={collaborator.id}
                collaborator={collaborator}
                permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
              />
            ))}
          </CollaboratorListContainer>
        </div>
      }
    />
  );
};

export interface ShareWorkflowStepModalProps {
  workflow: Pick<NonNullable<GetWorkflowQuery["workflow"]>, "id">;
  workflowStep: Pick<
    NonNullable<GetWorkflowQuery["workflow"]>["workflowSteps"][number],
    "id" | "name"
  >;
  onClose: () => void;
}

export const ShareWorkflowStepModalContent = ({
  workflowStep,
  workflow,
  onClose,
}: ShareWorkflowStepModalProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [currentView, setCurrentView] = useState<ModalView>("main");
  const [selectedInheritedGroup, setSelectedInheritedGroup] =
    useState<InheritedPermissionGroup | null>(null);

  const { data: stepData, loading: isLoading } =
    useWorkflowStepCollaboratorsQuery({
      variables: { workflowStepId: workflowStep.id },
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const [updateStepCollaborators, { loading: isSubmitting }] =
    useUpdateWorkflowStepCollaboratorsMutation();

  const collaboratorsData = useMemo(
    () => stepData?.workflowStep?.collaborators ?? [],
    [stepData?.workflowStep?.collaborators],
  );

  const initialCollaborators = useMemo(
    () => mapIndividualCollaboratorsFromStep(collaboratorsData),
    [collaboratorsData],
  );

  const groupPermissions = useMemo(
    () => mapGroupPermissionsFromStep(collaboratorsData),
    [collaboratorsData],
  );

  const currentCollaboratorsRef = useRef<Collaborator[]>([]);

  const handleStateChange = useCallback((collaborators: Collaborator[]) => {
    currentCollaboratorsRef.current = collaborators;
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      await updateStepCollaborators({
        variables: {
          data: {
            workflowId: workflow.id,
            workflowStepId: workflowStep.id,
            collaborators: currentCollaboratorsRef.current.map(c => ({
              entityId: c.id,
              entityType: AccessControlEntityType.PUBLIC_USER,
              policy: c.permission as AccessControlPolicy,
            })),
          },
        },
      });

      track("Shared Workflow Step", {
        workflowId: workflow.id,
        workflowStepId: workflowStep.id,
      });

      addSuccessToast(`This step has been updated successfully!`);
      onClose();
    } catch {
      addErrorToast(`Failed to update step`);
    }
  }, [
    workflow.id,
    workflowStep.id,
    updateStepCollaborators,
    addSuccessToast,
    addErrorToast,
    onClose,
  ]);

  const handleInheritedPermissionGroupClick = useCallback(
    (group: InheritedPermissionGroup) => {
      setSelectedInheritedGroup(group);
      setCurrentView("inheritedPermissions");
    },
    [],
  );

  const handleBackToMain = useCallback(() => {
    setCurrentView("main");
    setSelectedInheritedGroup(null);
  }, []);

  if (currentView === "inheritedPermissions" && selectedInheritedGroup) {
    return (
      <InheritedPermissionsView
        entityName={workflowStep.name}
        group={selectedInheritedGroup}
        onBack={handleBackToMain}
        onClose={onClose}
      />
    );
  }

  return (
    <CommonModal
      title="Share workflow step"
      subtitle={workflowStep.name}
      size="large"
      onCancel={onClose}
      primaryButtonText={isSubmitting ? "Saving..." : "Invite"}
      onSubmit={handleSubmit}
      disabled={isSubmitting || isLoading}
      secondaryButtons={[
        {
          text: "Done",
          onClick: onClose,
        },
      ]}
      content={
        isLoading ?
          <Loading size="medium" />
        : <ShareForm
            searchPlaceholder="Add email address"
            sectionLabel="Who has access"
            infoText={{
              text: `All Forerunner team members can view this step.`,
              linkText: "Add a team member",
              linkHref: "/settings/users/team",
            }}
            permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
            initialCollaborators={initialCollaborators}
            onStateChange={handleStateChange}
            buttonText="Add"
            includeUsers={false}
            includePublicUsers={true}
            inheritedPermissions={groupPermissions}
            onInheritedPermissionGroupClick={
              handleInheritedPermissionGroupClick
            }
          />
      }
    />
  );
};

export const useShareWorkflowStepModal = ({
  workflowStep,
  workflow,
}: Omit<ShareWorkflowStepModalProps, "onClose">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowStepModalContent
          workflowStep={workflowStep}
          workflow={workflow}
          onClose={hideModal}
        />
      </Modal>
    ),
    [workflowStep, workflow],
  );

  return [showModal, hideModal] as const;
};
